import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GiHamburgerMenu } from "react-icons/gi";
import { IoMdClose } from "react-icons/io";

import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";

import "./Navbar.css";

export default function Navbar() {
  const { t } = useTranslation("navbar");

  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const openMobileMenu = () => {
    setClosing(false);
    setOpen(true);
  };

  const closeMobileMenu = useCallback(() => {
    if (closing) {
      return;
    }

    setClosing(true);

    window.setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, 280);
  }, [closing]);

  /*
   * Mientras el menú móvil está abierto, bloqueamos
   * el desplazamiento del documento principal.
   */
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const previousOverflowX = document.body.style.overflowX;

    document.body.style.overflow = "hidden";
    document.body.style.overflowX = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.overflowX = previousOverflowX;
    };
  }, [open]);

  /*
   * Si el usuario agranda la ventana,
   * cerramos automáticamente el menú móvil.
   */
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1280 && open) {
        setOpen(false);
        setClosing(false);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [open]);

  /*
   * Permite cerrar el menú móvil con Escape.
   */
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeMobileMenu();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, closeMobileMenu]);

  const navItems = [
    {
      label: t("menu.aprende"),
      subLinks: [
        {
          text: t("menu.sub.cursos"),
          to: "/cursos",
        },
        {
          text: t("menu.sub.guias"),
          to: "/guiias",
        },
        {
          text: t("menu.sub.herramientas"),
          to: "/herramientas",
        },
      ],
    },
    {
      label: t("menu.explora"),
      subLinks: [
        {
          text: t("menu.sub.becas"),
          to: "/becas",
        },
        {
          text: t("menu.sub.viviendas"),
          to: "/viviendas",
        },
      ],
    },
    {
      label: t("menu.sobreZY"),
      subLinks: [
        {
          text: t("menu.sub.quienesSomos"),
          to: "/quienes-somos",
        },
        {
          text: t("menu.sub.faq"),
          to: "/preguntas-frecuentes",
        },
      ],
    },
    {
      label: t("menu.ayuda"),
      subLinks: [
        {
          text: t("menu.sub.contacto"),
          to: "/contacto",
        },
      ],
    },
  ];

  return (
    <>
      <header className="nz-navbar">
        <div className="nz-inner">
          <Link
            className="nz-logo nz-logo--text"
            to="/"
            aria-label="Ir al inicio"
            onClick={() => setActiveDropdown(null)}
          >
            ZY
          </Link>

          <nav className="nz-menu" aria-label="Navegación principal">
            {navItems.map((item, index) => {
              const isActive = activeDropdown === index;

              return (
                <div
                  key={item.label}
                  className={`nav-item ${isActive ? "is-open" : ""}`}
                  onMouseEnter={() => setActiveDropdown(index)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button
                    type="button"
                    className="dropdown-label"
                    aria-expanded={isActive}
                    onClick={() => {
                      setActiveDropdown((current) =>
                        current === index ? null : index,
                      );
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        setActiveDropdown(null);
                        event.currentTarget.blur();
                      }
                    }}
                  >
                    <span>{item.label}</span>
                    <i aria-hidden="true" />
                  </button>

                  <div className="dropdown-menu">
                    {item.subLinks.map((subLink) => (
                      <Link
                        key={subLink.text}
                        to={subLink.to}
                        onClick={() => setActiveDropdown(null)}
                      >
                        {subLink.text}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>

          <div className="nz-actions">
            <LanguageSwitcher className="language" variant="inline" />

            <button
              type="button"
              className="nz-burger"
              aria-label="Abrir menú"
              aria-expanded={open}
              aria-controls="zy-mobile-navigation"
              onClick={openMobileMenu}
            >
              <GiHamburgerMenu aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      {open ? (
        <aside
          id="zy-mobile-navigation"
          className={`nz-overlay ${closing ? "is-closing" : "is-open"}`}
          aria-label="Menú principal"
        >
          <div className="nz-overlay-header">
            {/* <Link
              className="nz-overlay-logo"
              to="/"
              aria-label="Ir al inicio"
              onClick={closeMobileMenu}
            >
              ZY
            </Link> */}

            <div className="nz-overlay-actions">
              <div className="nz-lang-mobile">
                <LanguageSwitcher variant="overlay" />
              </div>

              <button
                type="button"
                className="nz-close"
                aria-label="Cerrar menú"
                onClick={closeMobileMenu}
              >
                <IoMdClose aria-hidden="true" />
              </button>
            </div>
          </div>

          <nav
            className="nz-overlay-menu"
            aria-label="Navegación para dispositivos móviles"
          >
            {navItems.map((item) => (
              <div className="nz-overlay-group" key={item.label}>
                <span>{item.label}</span>

                <div>
                  {item.subLinks.map((subLink) => (
                    <Link
                      key={subLink.text}
                      to={subLink.to}
                      onClick={closeMobileMenu}
                    >
                      {subLink.text}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </aside>
      ) : null}
    </>
  );
}
